import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const RadarLogo = ({ className = "h-10 w-auto" }) => (
  <img src="/tabfinder-logo.png" alt="Tab Finder" className={className} />
);

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-heading text-xl font-semibold text-foreground mb-4">{title}</h2>
    <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 min-w-0">
              <RadarLogo className="h-9 w-auto shrink-0" />
            </Link>
            <Button variant="outline" size="sm" asChild>
              <Link to="/" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 max-w-3xl">
        <p className="text-sm text-muted-foreground mb-2">Last updated: April 6, 2026</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-8">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground text-sm leading-relaxed mb-12">
          Tab Finder (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the Tab Finder Chrome extension and the website{" "}
          <a href="https://tabfinder.io" className="text-primary underline underline-offset-2 hover:text-primary/80">
            tabfinder.io
          </a>
          . This policy describes how we collect, use, and share information when you use our website or extension.
        </p>

        <Section title="1. Scope">
          <p>
            This policy applies to information collected through the Tab Finder extension, our public website, and related services that link to this page.
            If you use third-party services (for example Google sign-in or a payment provider), their privacy policies also apply to how they process data.
          </p>
        </Section>

        <Section title="2. Information the extension processes">
          <p>
            <strong className="text-foreground">Tab and window metadata.</strong> To find duplicates and power search, the extension reads information
            about your open tabs and windows (such as URLs, titles, and tab identifiers) using Chrome&apos;s extension APIs. This processing happens on
            your device to provide core features. We do not upload your full browsing history or a list of every URL to our servers for analytics or
            advertising.
          </p>
          <p>
            <strong className="text-foreground">Local and synced storage.</strong> Preferences and similar settings may be stored with Chrome&apos;s{" "}
            <code className="text-foreground bg-muted px-1 py-0.5 rounded text-xs">storage</code> APIs on your device.
          </p>
        </Section>

        <Section title="3. Account sign-in (Google)">
          <p>
            Sign-in uses Google OAuth through Chrome&apos;s identity APIs. Depending on your sign-in flow and the permissions you approve, we may receive
            identifiers and profile details such as your Google account ID, email address, and basic profile information from Google. We use this to
            recognize your account, provide subscription or trial features tied to your identity, and support the service.
          </p>
          <p>
            Google&apos;s handling of data is described in{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:text-primary/80"
            >
              Google&apos;s Privacy Policy
            </a>
            .
          </p>
        </Section>

        <Section title="4. Our servers and service providers">
          <p>
            <strong className="text-foreground">Tab Finder API.</strong> When you are signed in, the extension may communicate with our backend at{" "}
            <code className="text-foreground bg-muted px-1 py-0.5 rounded text-xs">https://tabfinder.io/api</code> (and the same origin on{" "}
            <code className="text-foreground bg-muted px-1 py-0.5 rounded text-xs">tabfinder.io</code>) to verify subscription or trial status and related
            entitlements. Those requests may include an account or billing identifier derived from your sign-in—not your full browsing history.
          </p>
          <p>
            <strong className="text-foreground">Payments and billing.</strong> Checkout and customer billing may be handled by our payment processor
            (for example <strong className="text-foreground">Stripe</strong> when you open checkout or the billing portal). Stripe processes payment data
            under its own terms and privacy policy.
          </p>
          <p>
            <strong className="text-foreground">Website.</strong> When you visit <code className="text-foreground bg-muted px-1 py-0.5 rounded text-xs">tabfinder.io</code>, standard server and security logs may include IP address, user agent, and request metadata. We use this to operate and secure the site.
          </p>
        </Section>

        <Section title="5. How we use information">
          <ul className="list-disc pl-5 space-y-2">
            <li>Provide, maintain, and improve Tab Finder features.</li>
            <li>Authenticate you and manage trials or subscriptions.</li>
            <li>Respond to support requests and protect against abuse or technical issues.</li>
            <li>Comply with law and enforce our terms where applicable.</li>
          </ul>
        </Section>

        <Section title="6. Sharing">
          <p>
            We do not sell your personal information. We share data with service providers who help us run the service (hosting, authentication, payments)
            under appropriate agreements, and when required by law or to protect rights and safety.
          </p>
        </Section>

        <Section title="7. Retention">
          <p>
            We retain account and billing-related information as long as needed to provide the service and meet legal obligations. You may contact us to
            ask about deletion where applicable law allows.
          </p>
        </Section>

        <Section title="8. Children">
          <p>
            Tab Finder is not directed at children under 13 (or the minimum age in your jurisdiction). We do not knowingly collect personal information
            from children.
          </p>
        </Section>

        <Section title="9. International users">
          <p>
            If you use Tab Finder from outside the United States, your information may be processed in the United States or other countries where we or
            our providers operate.
          </p>
        </Section>

        <Section title="10. Changes">
          <p>
            We may update this policy from time to time. We will post the new version on this page and update the &ldquo;Last updated&rdquo; date. Continued
            use after changes means you accept the updated policy.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            For privacy questions or general support, use our{" "}
            <Link to="/contact" className="text-primary underline underline-offset-2 hover:text-primary/80">
              contact form
            </Link>{" "}
            at{" "}
            <a href="https://tabfinder.io/contact" className="text-primary underline underline-offset-2 hover:text-primary/80">
              tabfinder.io/contact
            </a>
            .
          </p>
        </Section>
      </main>

      <footer className="border-t border-border py-8 mt-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">
            ← Back to home
          </Link>
          <span>© {new Date().getFullYear()} Tab Finder</span>
        </div>
      </footer>
    </div>
  );
}
